import DuplicateModal from "@/components/core/modals/DuplicateModal";
import {
  requestCreateDuplicateModelMail,
  requestDeleteModelMail,
  requestSearchModelMail,
  requestUpdateModelMailDefault,
} from "@/state/modelMail/ModelMailEffects";
import { setSelectedOfficeForModelMail } from "@/state/modelMail/ModelMailEvents";
import { useModelMail } from "@/state/modelMail/ModelMailHook";
import { UserOffice } from "@/type/core/auth/auth.types";
import { ModelMail, ModelMailTypes } from "@/type/core/models/models.types";
import { toastError } from "@/utils/helpers/toast-helper";
import useOfficesToDisplay from "@/utils/hooks/useOfficesToDisplay";
import PageLayout from "@components/core/layouts/PageLayout";
import LinkButton from "@components/core/links/LinkButton";
import ConfirmModal from "@components/core/modals/ConfirmModal";
import InfoModal from "@components/core/modals/InfoModal";
import CardSection from "@components/core/section/CardSection";
import BasicTable from "@components/core/tables/BasicTable";
import BasicTabs from "@components/core/tabs/BasicTabs";
import { ROUTES } from "@routes/Routes";
import { columns } from "@views/core/extranet/menu/offices/mailModels/columns/MailModelsColumns";
import { Checkbox } from "antd";
import Search from "antd/es/input/Search";
import { FunctionComponent, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

interface Filters {
  invitationCheck: boolean;
  endOfTransactionCheck: boolean;
}

const MailModelsScreen: FunctionComponent = () => {
  const { t } = useTranslation();

  const { officesToDisplay, handleTabClick, activeKey } = useOfficesToDisplay();
  const { selectedOffice } = useModelMail();

  const [search, setSearch] = useState<string>("");
  const [selectedFilters, setSelectedFilters] = useState<Filters>({
    invitationCheck: true,
    endOfTransactionCheck: true,
  });
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState<boolean>(false);
  const [isDeleteModelOpen, setIsDeleteModelOpen] = useState<boolean>(false);
  const [isDuplicateModelOpen, setIsDuplicateModelOpen] =
    useState<boolean>(false);
  const [modelMails, setModelMails] = useState<ModelMail[]>([]);
  const [selectedModelMail, setSelectedModelMail] = useState<ModelMail>();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleSearch = useCallback(
    (officeId?: string) => {
      const types: ModelMailTypes[] = [];

      selectedFilters.invitationCheck && types.push("invitation");
      selectedFilters.endOfTransactionCheck && types.push("endOfTransaction");

      void requestSearchModelMail({
        dto: {
          officeId,
          page: 1,
          search,
          sort: { order: 0, type: "name" },
          types,
        },
      });
    },
    [search, selectedFilters],
  );

  useEffect(() => {
    setIsLoading(true);
    handleSearch(selectedOffice?.id);
  }, [selectedFilters, selectedOffice, search, handleSearch]);

  useEffect(() => {
    return requestSearchModelMail.done.watch(({ result }) => {
      if (result.ok && result.data) {
        setModelMails(result.data.content);
      } else {
        toastError(t("error.toast.modelMail.retrieve"));
      }
      setIsLoading(false);
    });
  });

  useEffect(() => {
    return requestUpdateModelMailDefault.done.watch(({ result }) => {
      if (result.ok) {
        handleSearch(selectedOffice?.id);
      } else {
        toastError(t("error.toast.modelMail.default"));
      }
    });
  });

  useEffect(() => {
    return requestCreateDuplicateModelMail.done.watch(({ result }) => {
      if (result.ok) {
        handleSearch(selectedOffice?.id);
      } else {
        toastError(t("error.toast.modelMail.duplicate"));
      }
    });
  });

  useEffect(() => {
    return requestDeleteModelMail.done.watch(({ result }) => {
      if (result.ok) {
        handleSearch(selectedOffice?.id);
      } else {
        toastError(t("error.toast.modelMail.delete"));
      }
    });
  });

  const handleSetDefault = (modelMailId: string) => {
    setIsLoading(true);
    void requestUpdateModelMailDefault({ dto: undefined, id: modelMailId });
  };

  const handleDeleteModel = () => {
    setIsLoading(true);
    setIsDeleteModelOpen(false);

    void requestDeleteModelMail({ id: selectedModelMail?.id });
  };

  const handleDuplicateModel = (name: string) => {
    setIsLoading(true);
    setIsDuplicateModelOpen(false);

    void requestCreateDuplicateModelMail({
      dto: { name },
      id: selectedModelMail?.id,
    });
  };

  const handleTabChange = (officeId?: string) => {
    const office = officesToDisplay.find((office) => office.id === officeId);
    setSelectedOfficeForModelMail(office);
  };

  return (
    <PageLayout container returnRoute={ROUTES.extranet.home.generate()}>
      <>
        <CardSection
          title={
            <>
              {t("offices.mailModel.title")}
              <InfoModal value="modelMail" info modalClassName="modal-lg" />
            </>
          }
        >
          <BasicTabs
            activeKey={(selectedOffice
              ? activeKey
              : officesToDisplay.length
            ).toString()}
            onTabClick={handleTabClick}
            onTabChange={handleTabChange}
            tabs={[
              ...officesToDisplay,
              { alias: t("Mes mails") } as UserOffice,
            ].map((office) => {
              return {
                title: office.alias,
                content: (
                  <>
                    <div className="d-flex flex-column flex-md-row align-items-center justify-content-center gap-24 mb-24">
                      <LinkButton
                        to={ROUTES.extranet.menu.offices.mailModel.add.generate(
                          "invitation",
                        )}
                        text={t("offices.mailModel.addInvitationModel")}
                        className="mw-340"
                      />
                      <span className="text-black font-large px-8">
                        {t("common.or")}
                      </span>
                      <LinkButton
                        to={ROUTES.extranet.menu.offices.mailModel.add.generate(
                          "endOfTransaction",
                        )}
                        text={t("offices.mailModel.addEndOfTransactionModel")}
                        className="mw-340"
                      />
                    </div>
                    <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-md-center gap-8 mb-24">
                      <span>{t("offices.mailModel.searchModelMails")}</span>
                      <Search
                        id="filterSearch"
                        placeholder={t("offices.mailModel.researchModel")}
                        value={search}
                        onChange={(e) => {
                          e.preventDefault();
                          setSearch(e.target.value);
                        }}
                        className="search ml-md-8 w-md-50"
                      />
                    </div>
                    <div className="d-flex align-items-center flex-wrap">
                      <Checkbox
                        id="invitationCheck"
                        checked={selectedFilters.invitationCheck}
                        onChange={() => {
                          !selectedFilters.endOfTransactionCheck &&
                          selectedFilters.invitationCheck
                            ? setIsConfirmModalOpen(true)
                            : setSelectedFilters({
                                ...selectedFilters,
                                invitationCheck:
                                  !selectedFilters.invitationCheck,
                              });
                        }}
                        className="mr-32 mb-24"
                      >
                        {t("offices.mailModel.type.invitation")}
                      </Checkbox>
                      <Checkbox
                        id="endOfTransactionCheck"
                        checked={selectedFilters.endOfTransactionCheck}
                        onChange={() => {
                          !selectedFilters.invitationCheck &&
                          selectedFilters.endOfTransactionCheck
                            ? setIsConfirmModalOpen(true)
                            : setSelectedFilters({
                                ...selectedFilters,
                                endOfTransactionCheck:
                                  !selectedFilters.endOfTransactionCheck,
                              });
                        }}
                        className="mb-24"
                      >
                        {t("offices.mailModel.type.endOfTransaction")}
                      </Checkbox>
                    </div>
                    <div className="min-height-table">
                      <BasicTable
                        isLoading={isLoading}
                        columns={columns(
                          setIsDeleteModelOpen,
                          setIsDuplicateModelOpen,
                          setSelectedModelMail,
                          handleSetDefault,
                        )}
                        dataSource={modelMails}
                      />
                    </div>
                  </>
                ),
                officeId: office.id,
              };
            })}
          />
        </CardSection>
        {selectedModelMail && (
          <ConfirmModal
            visible={isDeleteModelOpen}
            onCancel={() => setIsDeleteModelOpen(false)}
            onOk={handleDeleteModel}
            title={t("offices.mailModel.deleteModel")}
          />
        )}
        {selectedModelMail && (
          <DuplicateModal
            visible={isDuplicateModelOpen}
            onCancel={() => setIsDuplicateModelOpen(false)}
            onSubmit={handleDuplicateModel}
            previousName={selectedModelMail.name ?? ""}
          />
        )}
        <ConfirmModal
          visible={isConfirmModalOpen}
          onCancel={() => setIsConfirmModalOpen(false)}
          onOk={() => {
            setIsConfirmModalOpen(false);
            selectedFilters.invitationCheck
              ? setSelectedFilters({
                  ...selectedFilters,
                  invitationCheck: !selectedFilters.invitationCheck,
                })
              : setSelectedFilters({
                  ...selectedFilters,
                  endOfTransactionCheck: !selectedFilters.endOfTransactionCheck,
                });
          }}
          title={t("offices.mailModel.uncheckTypesModal.title")}
          content={t("offices.mailModel.uncheckTypesModal.body")}
        />
      </>
    </PageLayout>
  );
};

export default MailModelsScreen;
